import * as Yup from "yup";
import { useFormikContext } from "formik";
import { Stack } from "@mui/material";
import { BankIcon, Cash02Icon, CreditCardPosIcon } from "@hugeicons/core-free-icons";
import PaymentMethod from "@/pages/checkout/components/PaymentMethod";
import BankTransferOptions from "@/pages/checkout/components/BankTransferOptions";
import CreditCardDetails from "@/pages/checkout/components/CreditCardDetails";
import { bankOptions, creditCardOptions, currencyOptions } from "@/constants/paymentMethods";
import { useAppSelector } from "@/hooks";
import { Bank, Cash, Order, PaymentMethodsType } from "@/types";
import CashDetails from "@/pages/checkout/components/CashDetails";
import { RootState } from "@/store/store";
import useServiceFee from "@/hooks/useServiceFee";
import { getOrderTotal } from "@/utils/order";

export type IPaymentForm = {
  paymentDetails: Order["paymentDetails"];
};

export const paymentFormInitialValues = {
  paymentDetails: null,
};

export const PaymentFormSchema = Yup.object().shape({
  paymentDetails: Yup.object().required(),
});

const PaymentForm = () => {
  const { values, setFieldValue } = useFormikContext<IPaymentForm>();
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const organization = useAppSelector((store) => store.organizations?.[organizationId || ""]);
  const settings = organization?.settings;

  const order = useAppSelector((state: RootState) => state.order);
  const service = useServiceFee();

  const totalAmount = getOrderTotal(order.items, organization?.settings?.includeTax, service.fee);

  const groupedBankAccounts = settings?.paymentMethods?.bankTransfer?.reduce(
    (acc, current) => {
      return {
        ...acc,
        [current.bankName]: [...(acc[current.bankName] || []), { accountNumber: current.accountNumber, currency: current.currency }],
      };
    },
    {} as Record<string, { accountNumber: string; currency: string }[]>,
  );

  return (
    <Stack spacing={2}>
      {settings?.paymentMethods?.cash ? (
        <>
          <PaymentMethod label="Efectivo" icon={Cash02Icon} isChecked={values.paymentDetails?.method === PaymentMethodsType.CASH}>
            <CashDetails
              total={totalAmount}
              amount={(values.paymentDetails?.value as Cash)?.amount ?? ""}
              onAmountChange={(amount) =>
                setFieldValue("paymentDetails", {
                  method: PaymentMethodsType.CASH,
                  value: { amount },
                })
              }
            />
          </PaymentMethod>
        </>
      ) : (
        <></>
      )}
      {settings?.paymentMethods?.bankTransfer ? (
        <PaymentMethod label="Transferencia Bancaria" icon={BankIcon} isChecked={values.paymentDetails?.method === PaymentMethodsType.BANK_TRANSFER}>
          {groupedBankAccounts &&
            Object.entries(groupedBankAccounts).map(([bankId, accounts]) =>
              (accounts as Bank[]).map((account: Bank) => (
                <BankTransferOptions
                  key={account.accountNumber}
                  bankName={bankOptions.find((bank) => bank.value === bankId)?.label || bankId}
                  accountNumber={account.accountNumber}
                  currency={currencyOptions.find((cur) => cur.value === account.currency)?.label || account.currency}
                  isChecked={(values.paymentDetails?.value as Bank)?.accountNumber === account.accountNumber}
                  onChange={() =>
                    setFieldValue("paymentDetails", {
                      method: PaymentMethodsType.BANK_TRANSFER,
                      value: {
                        bankName: bankOptions.find((bank) => bank.value === bankId)?.label || bankId,
                        accountNumber: groupedBankAccounts[bankId][0].accountNumber,
                        currency: groupedBankAccounts[bankId][0].currency,
                      },
                    })
                  }
                />
              )),
            )}
        </PaymentMethod>
      ) : null}
      {settings?.paymentMethods?.stripeEnabled ? (
        <PaymentMethod
          label="Tarjeta de Crédito / Débito"
          icon={CreditCardPosIcon}
          isChecked={values?.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD}
          onChange={() => setFieldValue("paymentDetails", { method: PaymentMethodsType.CREDIT_CARD, value: null })}
        />
      ) : settings?.paymentMethods?.creditCard?.length ? (
        <PaymentMethod label="Tarjeta de Crédito" icon={CreditCardPosIcon} isChecked={values?.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD}>
          {settings.paymentMethods.creditCard.map((cardId: any, index: any) => (
            <CreditCardDetails
              key={index}
              label={creditCardOptions.find((card) => card.value === cardId)?.label || cardId}
              isChecked={values.paymentDetails?.value === cardId}
              onChange={() => setFieldValue("paymentDetails", { method: PaymentMethodsType.CREDIT_CARD, value: cardId })}
            />
          ))}
        </PaymentMethod>
      ) : null}
    </Stack>
  );
};

export default PaymentForm;
